import { onSnapshot, type Query, type QuerySnapshot } from 'firebase/firestore';

/**
 * A Firestore listener that doesn't stay dead.
 *
 * Every live query in this app used to fail the same way. Some had no error
 * handler at all, so one error ended the stream silently and permanently.
 * Others retried a fixed number of times and then gave up, also permanently.
 * Either way nothing tells the traveller: the tab keeps rendering whatever it
 * held when the stream stopped, so a settled expense doesn't move to Settled,
 * a saved shortlist item doesn't appear, and the only thing that fixes it is
 * a reload.
 *
 * That's the "changes don't show until I refresh" report, and it explains why
 * it looked intermittent — it depends on whether the stream happened to drop,
 * which on a phone switching networks or backgrounding an app it regularly
 * does.
 *
 * So: every error retries, with exponential backoff capped at 30 seconds, for
 * as long as the caller wants the listener. A successful snapshot resets the
 * backoff. The security-rules race right after a trip is created — where
 * isAcceptedMember's get() can't yet see the new trip — is simply the fast
 * end of the same curve rather than a special case.
 *
 * `restart()` exists for the other half: a mobile browser can drop a stream
 * while backgrounded without ever reporting an error, so callers reconnect on
 * re-foreground, which is also the moment a traveller would notice staleness.
 */
export interface LiveQueryHandle {
  /** Reconnect now, resetting the backoff. Safe to call on a healthy stream. */
  restart: () => void;
  stop: () => void;
}

export function liveQuery(
  label: string,
  build: () => Query,
  onData: (snapshot: QuerySnapshot) => void,
  onReady?: (ready: boolean) => void,
): LiveQueryHandle {
  let unsub = () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let attempt = 0;
  let generation = 0;

  const start = () => {
    if (stopped) return;
    const current = ++generation;
    onReady?.(false);
    if (timer) { clearTimeout(timer); timer = null; }
    unsub();
    unsub = onSnapshot(
      build(),
      { includeMetadataChanges: !!onReady },
      snapshot => {
        if (stopped || current !== generation) return;
        attempt = 0; onData(snapshot);
        onReady?.(!snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites);
      },
      error => {
        if (stopped || current !== generation) return;
        onReady?.(false);
        const wait = Math.min(1500 * 2 ** attempt, 30_000);
        attempt += 1;
        console.error(`[${label}] listener dropped (${error.code}) — reconnecting in ${wait}ms`);
        timer = setTimeout(start, wait);
      },
    );
  };

  start();

  return {
    restart: () => { attempt = 0; start(); },
    stop: () => { stopped = true; if (timer) clearTimeout(timer); unsub(); onReady?.(false); },
  };
}

/**
 * Reconnect a set of listeners whenever the tab comes back to the foreground.
 * Returns its own teardown. Costs one cached snapshot per listener when
 * nothing has changed, which is the cheapest possible way to be sure.
 */
export function reconnectOnForeground(handles: LiveQueryHandle[]): () => void {
  const onVisible = () => {
    if (document.visibilityState === 'visible') handles.forEach(handle => handle.restart());
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}
